const DAY_MS = 24 * 60 * 60 * 1000;
const CLOSED_STATUS_PATTERN = /跑路中|历史\/停运状态|停止运营|正式关站|站点已关闭|标记为已关闭/;

export const ANNIVERSARY_WINDOW_DAYS = 30;

export function parseCalendarDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return { year, month, day, epoch: date.valueOf() };
}

function todayParts(now) {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return { year, month, day, epoch: Date.UTC(year, month - 1, day) };
}

function calendarEpoch(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date.valueOf();
}

export function formatCalendarDate(value, language, { includeYear = true } = {}) {
  const parsed = parseCalendarDate(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    timeZone: 'UTC',
    ...(includeYear ? { year: 'numeric' } : {}),
    month: language === 'en' ? 'short' : 'long',
    day: 'numeric',
  }).format(new Date(parsed.epoch));
}

export function isSiteClosed(site, now = new Date()) {
  if (site?.monitor?.status === 'offline') return true;
  if (['online', 'down'].includes(site?.monitor?.status)) return false;
  if (CLOSED_STATUS_PATTERN.test(site?.registration ?? '')) return true;
  const closed = parseCalendarDate(site?.community_profile?.closed_at);
  return Boolean(closed && closed.epoch <= todayParts(now).epoch);
}

export function siteLifecycleState(site, now = new Date()) {
  if (site?.monitor?.status === 'down') return 'down';
  if (site?.monitor?.status === 'unknown' && !isSiteClosed(site, now)) return 'unknown';
  return isSiteClosed(site, now) ? 'dead' : 'alive';
}

export function nextAnniversary(site, now = new Date()) {
  const founded = parseCalendarDate(site.community_profile?.founded_at);
  if (!founded) return null;

  const today = todayParts(now);
  const closed = parseCalendarDate(site.community_profile?.closed_at);
  if (isSiteClosed(site, now)) return null;

  for (let year = Math.max(today.year, founded.year); year <= today.year + 8; year += 1) {
    const epoch = calendarEpoch(year, founded.month, founded.day);
    if (epoch === null || epoch < today.epoch) continue;
    if (closed && closed.epoch < epoch) return null;
    return {
      site,
      date: new Date(epoch),
      daysUntil: Math.round((epoch - today.epoch) / DAY_MS),
      years: year - founded.year,
    };
  }
  return null;
}

export function upcomingAnniversaries(sites, now = new Date(), windowDays = ANNIVERSARY_WINDOW_DAYS) {
  return sites
    .map((site) => nextAnniversary(site, now))
    .filter((entry) => entry && entry.daysUntil <= windowDays)
    .sort((left, right) => left.daysUntil - right.daysUntil || left.site.name.localeCompare(right.site.name, 'zh-CN'));
}
